// filepath: client/src/components/performance/PerformanceMonitor.tsx
import React, { useEffect } from 'react';
import { sendAnalyticsEvent } from '../../services/funnel';

interface PerformanceMonitorProps {
  enableReporting?: boolean;
  enableWebVitals?: boolean;
}

interface WebVitalsMetric {
  name: string;
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  delta: number;
  id: string;
}

const PerformanceMonitor: React.FC<PerformanceMonitorProps> = ({
  enableReporting = true,
  enableWebVitals = true
}) => {
  useEffect(() => {
    if (!enableReporting) return;

    // Only enable in production or when explicitly requested
    const shouldMonitor = (import.meta as any).env?.PROD ||
                         (import.meta as any).env?.VITE_ENABLE_PERFORMANCE_MONITORING === 'true';

    if (!shouldMonitor) {
      console.info('Performance monitoring disabled in development');
      return;
    }

    // Monitor Core Web Vitals
    if (enableWebVitals) {
      monitorWebVitals();
    }

    // Monitor custom performance metrics
    monitorCustomMetrics();

    // Monitor resource loading
    monitorResourceLoading();

    // Monitor user interactions
    monitorUserInteractions();

  }, [enableReporting, enableWebVitals]);

  return null; // This component doesn't render anything
};

// Core Web Vitals monitoring
const monitorWebVitals = () => {
  // Check supported entry types first
  const supportedEntryTypes = PerformanceObserver.supportedEntryTypes || [];

  // Largest Contentful Paint (LCP)
  if ('PerformanceObserver' in window && supportedEntryTypes.includes('largest-contentful-paint')) {
    const lcpObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const lastEntry = entries[entries.length - 1];

      const lcp = lastEntry.startTime;
      const rating = lcp <= 2500 ? 'good' : lcp <= 4000 ? 'needs-improvement' : 'poor';

      reportMetric({
        name: 'LCP',
        value: lcp,
        rating,
        delta: lcp,
        id: 'lcp-' + Date.now()
      });
    });

    try {
      lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });
    } catch (e) {
      console.warn('LCP monitoring failed to initialize:', e);
    }
  } else {
    console.info('LCP monitoring not supported in this browser');
  }

  // First Input Delay (FID)
  if ('PerformanceObserver' in window && supportedEntryTypes.includes('first-input')) {
    const fidObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach((entry: any) => {
        const fid = entry.processingStart - entry.startTime;
        const rating = fid <= 100 ? 'good' : fid <= 300 ? 'needs-improvement' : 'poor';

        reportMetric({
          name: 'FID',
          value: fid,
          rating,
          delta: fid,
          id: 'fid-' + Date.now()
        });
      });
    });

    try {
      fidObserver.observe({ entryTypes: ['first-input'] });
    } catch (e) {
      console.warn('FID monitoring failed to initialize:', e);
    }
  } else {
    console.info('FID monitoring not supported in this browser');
  }

  // Cumulative Layout Shift (CLS)
  if ('PerformanceObserver' in window) {
    let clsValue = 0;

    // Check if layout-shift is supported
    const supportedEntryTypes = PerformanceObserver.supportedEntryTypes || [];
    if (supportedEntryTypes.includes('layout-shift')) {
      const clsObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((entry: any) => {
          if (!entry.hadRecentInput) {
            clsValue += entry.value;
          }
        });

        const rating = clsValue <= 0.1 ? 'good' : clsValue <= 0.25 ? 'needs-improvement' : 'poor';

        reportMetric({
          name: 'CLS',
          value: clsValue,
          rating,
          delta: clsValue,
          id: 'cls-' + Date.now()
        });
      });

      try {
        clsObserver.observe({ entryTypes: ['layout-shift'] });
      } catch (e) {
        console.warn('CLS monitoring failed to initialize:', e);
      }
    } else {
      console.info('CLS monitoring not supported in this browser');
    }
  }
};

// Custom performance metrics
const monitorCustomMetrics = () => {
  // Time to Interactive (TTI) approximation
  window.addEventListener('load', () => {
    setTimeout(() => {
      const tti = performance.now();
      const rating = tti <= 3800 ? 'good' : tti <= 7300 ? 'needs-improvement' : 'poor';
      
      reportMetric({
        name: 'TTI',
        value: tti,
        rating,
        delta: tti,
        id: 'tti-' + Date.now()
      });
    }, 0);
  });

  // First Contentful Paint (FCP)
  if ('PerformanceObserver' in window) {
    const fcpObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach((entry) => {
        if (entry.name === 'first-contentful-paint') {
          const fcp = entry.startTime;
          const rating = fcp <= 1800 ? 'good' : fcp <= 3000 ? 'needs-improvement' : 'poor';
          
          reportMetric({
            name: 'FCP',
            value: fcp,
            rating,
            delta: fcp,
            id: 'fcp-' + Date.now()
          });
        }
      });
    });

    try {
      fcpObserver.observe({ entryTypes: ['paint'] });
    } catch (e) {
      console.warn('FCP monitoring not supported');
    }
  }

  // Navigation timing
  window.addEventListener('load', () => {
    const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
    if (navigation) {
      const metrics = {
        'DNS Lookup': navigation.domainLookupEnd - navigation.domainLookupStart,
        'TCP Connection': navigation.connectEnd - navigation.connectStart,
        'Server Response': navigation.responseStart - navigation.requestStart,
        'DOM Content Loaded': navigation.domContentLoadedEventEnd - (navigation as any).navigationStart,
        'Page Load Complete': navigation.loadEventEnd - (navigation as any).navigationStart
      };

      Object.entries(metrics).forEach(([name, value]) => {
        reportMetric({
          name,
          value,
          rating: 'good', // These are informational
          delta: value,
          id: name.toLowerCase().replace(/\s+/g, '-') + '-' + Date.now()
        });
      });
    }
  });
};

// Resource loading monitoring
const monitorResourceLoading = () => {
  if ('PerformanceObserver' in window) {
    const resourceObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach((entry) => {
        const resource = entry as PerformanceResourceTiming;
        
        // Monitor slow resources
        const loadTime = resource.responseEnd - resource.startTime;
        if (loadTime > 1000) { // Resources taking more than 1 second
          console.warn(`Slow resource detected: ${resource.name} took ${loadTime.toFixed(2)}ms`);
          
          reportMetric({
            name: 'Slow Resource',
            value: loadTime,
            rating: 'poor',
            delta: loadTime,
            id: 'slow-resource-' + Date.now()
          });
        }
      });
    });

    try {
      resourceObserver.observe({ entryTypes: ['resource'] });
    } catch (e) {
      console.warn('Resource monitoring not supported');
    }
  }
};

// User interaction monitoring
const monitorUserInteractions = () => {
  let interactionCount = 0;
  
  const trackInteraction = (type: string) => {
    interactionCount++;
    
    // Track interaction responsiveness
    const startTime = performance.now();
    
    requestAnimationFrame(() => {
      const endTime = performance.now();
      const responseTime = endTime - startTime;
      
      if (responseTime > 100) { // Slow interaction
        reportMetric({
          name: 'Slow Interaction',
          value: responseTime,
          rating: 'poor',
          delta: responseTime,
          id: 'slow-interaction-' + Date.now()
        });
      }
    });
  };

  // Track clicks
  document.addEventListener('click', () => trackInteraction('click'));
  
  // Track form submissions
  document.addEventListener('submit', () => trackInteraction('submit'));
  
  // Track navigation
  window.addEventListener('popstate', () => trackInteraction('navigation'));
};

// Report metrics to analytics
const reportMetric = (metric: WebVitalsMetric) => {
  // Log to console in development
  if ((import.meta as any).env?.DEV) {
    console.log(`Performance Metric - ${metric.name}:`, {
      value: `${metric.value.toFixed(2)}ms`,
      rating: metric.rating,
      id: metric.id
    });
  }

  // Send to Google Analytics
  sendAnalyticsEvent(metric.name, {
    event_category: 'Web Vitals',
    event_label: metric.id,
    value: Math.round(metric.value),
    metric_rating: metric.rating,
  });

  // Send to custom analytics endpoint (if needed)
  if ((import.meta as any).env?.PROD) {
    // You can implement custom reporting here
    // fetch('/api/analytics/performance', {
    //   method: 'POST',
    //   headers: { 'Content-Type': 'application/json' },
    //   body: JSON.stringify(metric)
    // });
  }
};

export default PerformanceMonitor;
